import { useRef, useEffect } from 'react';
import { getUrlAssets } from '../constants';

const START_TIMEOUT_MS = 4000;
const STALL_RECOVERY_MS = 4000;

const buildAnimationVideoUrl = (videoPath) => {
  if(!videoPath){ return null; }
  if(/^https?:\/\//i.test(videoPath)){ return videoPath; }
  return getUrlAssets() + videoPath;
};

export default function VideoAnimation({ onCompleteAnimation, videoPath }){
  const videoRef = useRef(null);
  useEffect(() => {
    const video = videoRef.current;
    let completed = false;
    let started = false;
    let disposed = false;
    let startTimer = null;
    let stallTimer = null;
    let lastBufferedEnd = 0;

    const clearStartTimer = () => { if(startTimer){ clearTimeout(startTimer); startTimer = null; } };
    const clearStallTimer = () => { if(stallTimer){ clearTimeout(stallTimer); stallTimer = null; } };

    const complete = () => {
      if(completed || disposed){ return; }
      completed = true;
      clearStartTimer();
      clearStallTimer();
      onCompleteAnimation();
    };

    if(!video){ complete(); return () => {}; }

    const getBufferedEnd = () => {
      try{
        if(video.buffered && video.buffered.length > 0){
          return video.buffered.end(video.buffered.length - 1);
        }
      }catch(error){ return 0; }
      return 0;
    };
    const hasRealProgress = () => {
      const end = getBufferedEnd();
      if(end > lastBufferedEnd){ lastBufferedEnd = end; return true; }
      return false;
    };
    const armStartTimer = () => {
      clearStartTimer();
      startTimer = setTimeout(() => {
        if(started){ return; }
        if(hasRealProgress()){ armStartTimer(); return; }
        complete();
      }, START_TIMEOUT_MS);
    };
    const armStallTimer = () => {
      clearStallTimer();
      stallTimer = setTimeout(() => {
        if(hasRealProgress()){ armStallTimer(); return; }
        complete();
      }, STALL_RECOVERY_MS);
    };

    const onProgress = () => {
      if(!hasRealProgress()){ return; }
      if(!started){ armStartTimer(); }
      else if(stallTimer){ armStallTimer(); }
    };
    const onPlaying = () => { started = true; clearStartTimer(); clearStallTimer(); };
    const onEnded = () => { complete(); };
    const onError = () => { complete(); };
    const onWaiting = () => { if(started){ armStallTimer(); } };

    video.addEventListener('progress', onProgress);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    video.addEventListener('waiting', onWaiting);

    lastBufferedEnd = getBufferedEnd();
    armStartTimer();

    const playMuted = () => {
      if(disposed){ return; }
      video.muted = true;
      const mutedPromise = video.play();
      if(mutedPromise && typeof mutedPromise.catch === 'function'){
        mutedPromise.catch((error) => {
          if(error && error.name === 'AbortError' && disposed){ return; }
          complete();
        });
      }
    };

    const onSoundRejected = (error) => {
      if(error && error.name === 'AbortError' && disposed){ return; }
      playMuted();
    };

    video.muted = false;
    const soundPromise = video.play();
    if(soundPromise && typeof soundPromise.catch === 'function'){
      soundPromise.catch(onSoundRejected);
    }

    return () => {
      disposed = true;
      clearStartTimer();
      clearStallTimer();
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
      video.removeEventListener('waiting', onWaiting);
      try{ video.pause(); }catch(error){ /* noop */ }
    };
  }, [videoPath]);
  return (
    <div className={"loading-detail loading-video"}>
      <div className={"modal-title"}>
        <video ref={videoRef} src={buildAnimationVideoUrl(videoPath)} preload="auto" playsInline />
      </div>
    </div>
  );
}
